/**
 * PVWordExport — Exporte le HTML d'un PV en fichier Word (.doc HTML).
 *
 * Word ouvre parfaitement un HTML avec le bon en-tête MSO conditionnel, MAIS
 * il ne supporte pas :
 *   - `display: table / table-cell` en CSS  → à convertir en vrais <table>/<td>
 *   - Les images en URL relative           → à embarquer en data URI base64
 *   - `nth-child(even)` et sélecteurs      → à inliner au cas par cas
 *   - Les classes CSS complexes            → à inliner sur les éléments cibles
 *
 * Cette version applique ces transformations sur le HTML des modèles PV avant
 * de l'encapsuler dans le squelette Word HTML.
 */

// ── Résolution du logo pour Word ────────────────────────────────────────────
// Word .doc HTML NE SUPPORTE PAS les data URI dans les <img>. On utilise donc
// une URL ABSOLUE basée sur l'origine actuelle — Word ira charger l'image via
// HTTP quand le document sera ouvert (fonctionne tant que le PC a accès au
// serveur). C'est la seule option viable pour le format .doc HTML ; un vrai
// .docx (ZIP OOXML) permettrait d'embarquer, mais ajoute une lib lourde.
function resolveLogoAbsoluteUrl(relative = '/logo.png'): string {
  try {
    if (typeof window !== 'undefined' && window.location?.origin) {
      return window.location.origin + relative;
    }
  } catch { /* SSR */ }
  return relative;
}

// ── Transformations HTML → Word-friendly ────────────────────────────────────

/**
 * Convertit les blocs `display:table/table-cell` (utilisés dans les modèles PV
 * pour créer des colonnes) en vraies balises <table>/<tr>/<td>. Word rend
 * correctement les <table>, pas les CSS display:table.
 *
 * Cibles :
 *   - <div class="entete"> ... </div> avec .entete-cell.col-gauche + .col-droite
 *   - Autres divs marqués display:table dans un style inline
 */
function convertDivTablesToRealTables(doc: Document): void {
  // Cas spécifique : le bloc .entete du modèle ministériel (bordé)
  doc.querySelectorAll('div.entete').forEach((div) => {
    const cells = Array.from(div.querySelectorAll(':scope > .entete-cell'));
    if (cells.length < 2) return;
    const table = doc.createElement('table');
    table.setAttribute(
      'style',
      'width:100%;border-collapse:collapse;border:1px solid #000;margin-bottom:6px;',
    );
    const tr = doc.createElement('tr');
    cells.forEach((cell) => {
      const td = doc.createElement('td');
      const isGauche = cell.classList.contains('col-gauche');
      td.setAttribute(
        'style',
        `vertical-align:middle;padding:8px 12px;${
          isGauche
            ? 'width:60%;border-right:1px solid #000;font-size:10pt;line-height:1.4;'
            : 'width:40%;text-align:center;'
        }`,
      );
      td.innerHTML = cell.innerHTML;
      tr.appendChild(td);
    });
    table.appendChild(tr);
    div.replaceWith(table);
  });

  // Fallback plus générique : n'importe quel div avec `display:table` inline
  doc.querySelectorAll('div[style*="display:table"], div[style*="display: table"]').forEach((div) => {
    const cellSelector = ':scope > [style*="display:table-cell"], :scope > [style*="display: table-cell"]';
    const cells = Array.from(div.querySelectorAll(cellSelector));
    if (cells.length < 1) return;
    const table = doc.createElement('table');
    table.setAttribute(
      'style',
      'width:100%;border-collapse:collapse;' + (div.getAttribute('style') || ''),
    );
    const tr = doc.createElement('tr');
    cells.forEach((cell) => {
      const td = doc.createElement('td');
      td.setAttribute(
        'style',
        'vertical-align:middle;' + (cell.getAttribute('style') || ''),
      );
      td.innerHTML = cell.innerHTML;
      tr.appendChild(td);
    });
    table.appendChild(tr);
    div.replaceWith(table);
  });
}

/**
 * Convertit les <div class="bandeau-classe"> en une petite table bordée
 * (Word rend un div bordé de façon capricieuse quand il déborde).
 */
function convertBandeauClasse(doc: Document): void {
  doc.querySelectorAll('div.bandeau-classe').forEach((div) => {
    const table = doc.createElement('table');
    table.setAttribute('style', 'width:100%;border-collapse:collapse;margin:6px 0 10px;');
    const tr = doc.createElement('tr');
    const td = doc.createElement('td');
    td.setAttribute(
      'style',
      'border:1px solid #000;background:#f6f6f6;padding:7px 10px;text-align:center;font-size:11pt;',
    );
    td.innerHTML = div.innerHTML;
    tr.appendChild(td);
    table.appendChild(tr);
    div.replaceWith(table);
  });
}

/**
 * Force border, padding, background sur les cellules des tables statistiques
 * ET des tables de validés (les classes CSS ne remontent pas de façon fiable
 * dans Word).
 */
function inlineTableStyles(doc: Document): void {
  // Tables stats / valides du modèle ministériel
  const tableSelectors = [
    { sel: 'table.stats-table', width: '65%', center: true, headerBg: '#ececec' },
    { sel: 'table.valides-table', width: '100%', center: false, headerBg: '#ececec' },
    // Fallback pour tout <table> sans style border
    { sel: 'table:not([data-word-styled])', width: null, center: false, headerBg: '#ececec' },
  ];

  tableSelectors.forEach(({ sel, width, center, headerBg }) => {
    doc.querySelectorAll(sel).forEach((table) => {
      if (table.getAttribute('data-word-styled')) return;
      table.setAttribute('data-word-styled', '1');
      const existingStyle = table.getAttribute('style') || '';
      const widthCss = width ? `width:${width};` : (existingStyle.includes('width') ? '' : 'width:auto;');
      const marginCss = center ? 'margin:8px auto;' : (existingStyle.includes('margin') ? '' : 'margin:6px 0;');
      table.setAttribute(
        'style',
        `border-collapse:collapse;${widthCss}${marginCss}${existingStyle}`,
      );
      table.setAttribute('border', '1');
      table.setAttribute('cellspacing', '0');
      table.setAttribute('cellpadding', '4');

      // Header
      table.querySelectorAll('thead th, th').forEach((th) => {
        const s = th.getAttribute('style') || '';
        th.setAttribute(
          'style',
          `border:1px solid #000;padding:5px 8px;background:${headerBg};text-align:center;font-weight:bold;font-size:10.5pt;${s}`,
        );
      });
      // Body cells
      table.querySelectorAll('tbody td, td').forEach((td) => {
        const s = td.getAttribute('style') || '';
        // Ne pas écraser une bordure déjà définie
        if (!s.includes('border')) {
          td.setAttribute('style', `border:1px solid #000;padding:5px 8px;font-size:10.5pt;${s}`);
        } else if (!s.includes('padding')) {
          td.setAttribute('style', `padding:5px 8px;${s}`);
        }
      });
    });
  });
}

/**
 * Remplace tous les src relatifs par des URL absolues (pointant vers le
 * serveur d'origine). Word .doc HTML n'accepte pas les data URI et ne résout
 * pas les chemins relatifs — seule une URL absolue http(s):// est chargée.
 */
function absoluteImageUrls(doc: Document): void {
  doc.querySelectorAll('img').forEach((img) => {
    const src = img.getAttribute('src') || '';
    if (src.startsWith('data:') || src.includes('://')) return;
    // Chemin relatif : préfixe par window.location.origin
    const absolute = resolveLogoAbsoluteUrl(src.startsWith('/') ? src : '/' + src);
    img.setAttribute('src', absolute);
    img.removeAttribute('onerror'); // évite un warning Word
  });
}

/**
 * Inline les règles CSS de classe (.foo, .bar) sur les éléments matchant.
 *
 * Word .doc HTML lit les <style> de manière très imparfaite : de nombreux
 * sélecteurs de classe sont ignorés selon la version. Pour un rendu fidèle,
 * on parse les règles simples (.classe / tag.classe) et on écrit chaque
 * déclaration en style="" INLINE sur les éléments cibles. Les styles inline
 * existants gardent priorité (concaténés à la fin).
 *
 * Skip : @-rules (@media/@page), pseudo-classes complexes (nth-child, hover,
 * before/after) — Word ne les gère pas de toute façon.
 */
function inlineClassCSS(doc: Document): void {
  const styleTags = Array.from(doc.querySelectorAll('style'));
  styleTags.forEach((styleTag) => {
    const css = styleTag.textContent || '';
    // Retire les commentaires CSS
    const clean = css.replace(/\/\*[\s\S]*?\*\//g, '');
    // Parse rules brutalement : `selectors { declarations }`
    const ruleRe = /([^{}]+)\{([^{}]+)\}/g;
    let m: RegExpExecArray | null;
    while ((m = ruleRe.exec(clean)) !== null) {
      const selectors = m[1].trim();
      const declarations = m[2].trim();
      if (!selectors || !declarations) continue;
      if (selectors.startsWith('@')) continue; // @media / @page / @keyframes
      selectors.split(',').map((s) => s.trim()).filter(Boolean).forEach((sel) => {
        // Skip sélecteurs Word ne gère pas
        if (/::|:hover|:focus|:active|:nth-|:first-|:last-|:not\(|:is\(|:where\(|>|\+|~/.test(sel)) return;
        try {
          const elems = doc.querySelectorAll(sel);
          elems.forEach((el) => {
            const existing = el.getAttribute('style') || '';
            // Règle CSS AVANT le style inline existant → inline garde priorité
            const merged = declarations.replace(/\s+/g, ' ')
              + (existing && !existing.startsWith(';') ? '; ' : '') + existing;
            el.setAttribute('style', merged);
          });
        } catch { /* sélecteur invalide */ }
      });
    }
  });
}

/**
 * Convertit le HTML d'un modèle PV en HTML compatible Word.
 *
 * Ordre des transformations IMPORTANT :
 *   1. inline CSS classes → écrit styles inline (donc lus par Word)
 *   2. convert display:table → vraies <table> (avant que la table.stats-table
 *      styling ne mette de bordures partout)
 *   3. bandeau-classe → table bordée
 *   4. inline styles tables (bordures forcées)
 *   5. URL absolues sur images
 */
function htmlToWordFriendly(rawHtml: string): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(rawHtml, 'text/html');
  inlineClassCSS(doc);           // 1) tous les .titre/.para/etc. deviennent style=""
  convertDivTablesToRealTables(doc); // 2) div.entete → <table>
  convertBandeauClasse(doc);         // 3) div.bandeau-classe → <table>
  inlineTableStyles(doc);            // 4) force borders sur toutes tables
  absoluteImageUrls(doc);            // 5) URL absolue pour <img>
  return '<!DOCTYPE html>' + doc.documentElement.outerHTML;
}

/** Détecte l'orientation depuis le premier @page. */
function detectOrientation(html: string): 'portrait' | 'landscape' {
  const m = html.match(/@page\s*\{[^}]*?size\s*:\s*[^;]*?(landscape|portrait)/i);
  return (m?.[1]?.toLowerCase() as 'portrait' | 'landscape') || 'portrait';
}

/**
 * Enveloppe le HTML (transformé Word-friendly) dans un document Word HTML
 * et déclenche le téléchargement du .doc.
 */
export async function exportPVAsWord(html: string, filename: string): Promise<void> {
  // Applique les transformations DOM (synchrone maintenant : logo = URL absolue)
  const friendlyHtml = htmlToWordFriendly(html);

  const orientation = detectOrientation(friendlyHtml);
  const pageSize = orientation === 'landscape' ? '29.7cm 21cm' : '21cm 29.7cm';

  // Extrait le <body>...</body> pour l'insérer dans notre wrapper Word
  const bodyMatch = friendlyHtml.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
  const bodyContent = bodyMatch ? bodyMatch[1] : friendlyHtml;
  // On récupère les <style> pour les injecter dans notre <head> Word
  const styles: string[] = [];
  const styleRe = /<style[^>]*>([\s\S]*?)<\/style>/gi;
  let sm: RegExpExecArray | null;
  while ((sm = styleRe.exec(friendlyHtml)) !== null) {
    styles.push(sm[1]);
  }
  const collectedStyles = styles.join('\n');

  const wordDoc =
    `<html xmlns:o="urn:schemas-microsoft-com:office:office" ` +
    `xmlns:w="urn:schemas-microsoft-com:office:word" ` +
    `xmlns="http://www.w3.org/TR/REC-html40">` +
    `<head>` +
    `<meta charset="utf-8">` +
    `<meta http-equiv="Content-Type" content="text/html; charset=utf-8">` +
    `<title>${filename}</title>` +
    `<!--[if gte mso 9]><xml>` +
    `<w:WordDocument>` +
    `<w:View>Print</w:View>` +
    `<w:Zoom>100</w:Zoom>` +
    `<w:DoNotOptimizeForBrowser/>` +
    `</w:WordDocument>` +
    `</xml><![endif]-->` +
    `<style>` +
    `@page WordSection1 {` +
    `  size: ${pageSize};` +
    `  margin: 1.3cm 1.6cm 1.3cm 1.6cm;` +
    `  mso-header-margin: 0.5cm;` +
    `  mso-footer-margin: 0.5cm;` +
    `  mso-paper-source: 0;` +
    `}` +
    `div.WordSection1 { page: WordSection1; }` +
    `body { font-family: 'Times New Roman', Times, serif; font-size: 11pt; margin: 0; color: #000; }` +
    `p { margin: 6pt 0; }` +
    `table { border-collapse: collapse; }` +
    `td, th { vertical-align: top; }` +
    `${collectedStyles}` +
    `</style>` +
    `</head>` +
    `<body>` +
    `<div class="WordSection1">${bodyContent}</div>` +
    `</body></html>`;

  const bom = '﻿';
  const blob = new Blob([bom + wordDoc], {
    type: 'application/vnd.ms-word;charset=utf-8',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename.toLowerCase().endsWith('.doc') ? filename : `${filename}.doc`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 100);
}

/** Compose un nom de fichier propre pour un PV. */
export function buildPVFilename(
  classeCode: string,
  semestre: number | string,
  annee: string,
  session: string,
  dateISO?: string,
): string {
  const cleanClasse = (classeCode || 'CLASSE').replace(/[^A-Za-z0-9_-]/g, '');
  const cleanSession = (session || 'ordinaire').replace(/[^a-z]/gi, '').toLowerCase();
  const cleanAnnee = (annee || '').replace(/[^0-9-]/g, '');
  const cleanDate = (dateISO || '').slice(0, 10).replace(/[^0-9-]/g, '');
  const parts = ['PV', cleanClasse, `S${semestre}`, cleanAnnee, cleanSession];
  if (cleanDate) parts.push(cleanDate);
  return parts.filter(Boolean).join('_');
}
